import type { RequestReportForm } from "@nlc/shared-types";
import type React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { requestReportFormZod } from "@nlc/shared-types";
import { Controller, useForm } from "react-hook-form";

import { Button } from "@/components/button";
import {
  Checkbox,
  Checkboxes,
  CheckboxField,
  CheckboxGroup,
} from "@/components/checkbox";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
} from "@/components/dialog";
import { Label, TextArea, TextField } from "@/components/field";
import { Modal } from "@/components/modal";
import { toast } from "@/components/toast/toast-queue.ts";
import { useRequestReport } from "@/features/request/edit/hooks/useRequestReport";
import { useIsTemplate } from "@/hooks/useIsTemplate.tsx";

interface SubmitWarningModalProps {
  isOpen: boolean;
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
  onOpenUnlock: () => void;
}

export function HavingProblemsModal({
  isOpen,
  onOpenChange,
  onOpenUnlock,
}: SubmitWarningModalProps) {
  const { isPending, mutate: submitReport } = useRequestReport();
  const { isTemplate } = useIsTemplate();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<RequestReportForm>({
    defaultValues: {
      report: "",
      type: [],
    },
    resolver: zodResolver(requestReportFormZod),
  });

  const onSubmit = (data: RequestReportForm) => {
    if (isTemplate) {
      onOpenUnlock();
      onOpenChange(false);

      return;
    }
    submitReport(data, {
      onError: () => {
        toast.add({
          description: "Please try again later",
          timeout: 2000,
          title: "Error submitting report",
          type: "error",
        });
      },
      onSuccess: () => {
        toast.add({
          description: "Thank you for your feedback! We will review it.",
          timeout: 2000,
          title: "Report submitted successfully",
          type: "success",
        });
        reset();
        onOpenChange(false);
      },
    });
  };

  return (
    <Modal
      classNames={{ modal: "w-[507px]" }}
      isDismissable
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          reset();
        }
        onOpenChange(open);
      }}
    >
      <Dialog>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogBody>
            <div className="flex flex-col gap-xs">
              <span className="text-lg font-semibold text-text-primary">
                Having problems? Let us know!
              </span>
              <DialogCloseButton />
              <span className="text-sm text-text-tertiary">
                {/* eslint-disable-next-line react/no-unescaped-entities */}
                We're here to help. Please share your feedback and let us know
                what has been causing an issue.
              </span>
            </div>
            <Controller
              control={control}
              name="type"
              render={({ field }) => (
                <CheckboxGroup
                  isRequired
                  onChange={field.onChange}
                  value={field.value}
                >
                  <Checkboxes>
                    <CheckboxField>
                      <Checkbox value="Quality concerns">
                        Quality concerns
                      </Checkbox>
                    </CheckboxField>
                    <CheckboxField>
                      <Checkbox value="Poor communication">
                        Poor communication
                      </Checkbox>
                    </CheckboxField>
                    <CheckboxField>
                      <Checkbox value="Off-brand">Off-brand</Checkbox>
                    </CheckboxField>
                    <CheckboxField>
                      <Checkbox value="Many rounds of revisions">
                        Many rounds of revisions
                      </Checkbox>
                    </CheckboxField>
                    <CheckboxField>
                      <Checkbox value="Misinterpretation of directions">
                        Misinterpretation of directions
                      </Checkbox>
                    </CheckboxField>
                    <CheckboxField>
                      <Checkbox value="Extended turnaround time">
                        Extended turnaround time
                      </Checkbox>
                    </CheckboxField>
                    <CheckboxField>
                      <Checkbox value="Other">Other</Checkbox>
                    </CheckboxField>
                  </Checkboxes>
                </CheckboxGroup>
              )}
            />
            {errors.type && (
              <p className="text-sm text-red-500 mt-1">{errors.type.message}</p>
            )}
            <Controller
              control={control}
              name="report"
              render={({ field }) => (
                <TextField className="mt-lg">
                  <Label htmlFor="additionalFeedback">
                    Leave Feedback (Optional)
                  </Label>
                  <TextArea
                    {...field}
                    placeholder="Additional feedback is optional but highly recommended"
                    rows={5}
                  />
                </TextField>
              )}
            />
          </DialogBody>
          <DialogFooter className="flex gap-md">
            <Button
              className="flex-1"
              color="gray"
              onPress={() => onOpenChange(false)}
              size="lg"
              type="button"
              variant="secondary"
            >
              Skip
            </Button>
            <Button
              className="flex-1"
              isDisabled={isPending}
              size="lg"
              type="submit"
              variant="primary"
            >
              {isPending ? "Submitting..." : "Submit"}
            </Button>
          </DialogFooter>
        </form>
      </Dialog>
    </Modal>
  );
}
